/**
 * Sign-in failure reporting (board PB-90, D135): the steps we report and the
 * Firebase errors that are the user's own mistake (not reported). Shared by
 * the browser (`src/services/authFailureLog.ts`) and the alert
 * (`onAuthFailureCreated`), so both read the same words. A new step is one
 * entry here.
 */
export const AUTH_FAILURE_STAGES = {
  password_sign_in: 'Sign in with email and password',
  google_sign_in: 'Sign in with Google',
  apple_sign_in: 'Sign in with Apple',
  second_step_send: 'Send the sign-in text code',
  second_step_verify: 'Check the sign-in code',
  text_setup_send: 'Send the code to add a phone',
  text_setup_confirm: 'Check the code to add a phone',
  confirm_identity: 'Confirm it’s you',
  accept_invitation: 'Accept an invitation',
} as const

export type AuthFailureStage = keyof typeof AUTH_FAILURE_STAGES

export function authFailureStageLabel(stage?: string): string {
  return (
    AUTH_FAILURE_STAGES[stage as AuthFailureStage] ?? stage ?? 'Unknown step'
  )
}

/**
 * Firebase codes that mean the person made a mistake or chose to stop:
 * wrong password or code, a mistyped number, closing the Google window.
 * Not reported. Everything else, including errors with no code, is.
 */
export const USER_ERROR_CODES: ReadonlySet<string> = new Set([
  'auth/wrong-password',
  'auth/invalid-credential',
  'auth/invalid-login-credentials',
  'auth/user-not-found',
  'auth/invalid-email',
  'auth/missing-password',
  'auth/weak-password',
  'auth/email-already-in-use',
  'auth/invalid-verification-code',
  'auth/missing-verification-code',
  'auth/code-expired',
  'auth/invalid-phone-number',
  'auth/missing-phone-number',
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
  'auth/account-exists-with-different-credential',
  // Steps the app handles itself (asks for the code, or to confirm it's you)
  'auth/multi-factor-auth-required',
  'auth/requires-recent-login',
])
